"use client";

import { useEffect, useMemo, useState } from "react";
import { useMessages } from "next-intl";

import type { SiteCopy } from "@/lib/site-data";
import { usePreferences } from "@/components/site/preferences-provider";
import { Button } from "@/components/ui/button";

type LocationSuggestion = {
  id: string;
  kind: "country" | "city";
  label: string;
  countryCode: string | null;
  countryName: string | null;
  cityName: string | null;
  latitude: number;
  longitude: number;
};

type EventLocationSelectionLike = {
  countryCode: string | null;
  countryName: string | null;
  cityName: string | null;
  cityLabel: string | null;
  latitude: number | null;
  longitude: number | null;
};

function normalize(value: string): string {
  return value.trim();
}

function formatLocationLabel(location: EventLocationSelectionLike): string {
  if (location.cityLabel || location.cityName) {
    const parts = [location.cityLabel ?? location.cityName, location.countryName].filter(Boolean);
    return parts.join(", ");
  }

  return location.countryName ?? location.countryCode?.toUpperCase() ?? "";
}

function useLocationSearch(query: string, enabled: boolean) {
  const [results, setResults] = useState<LocationSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const search = normalize(query);
  const searchActive = enabled && search.length >= 2;

  useEffect(() => {
    if (!searchActive) {
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      try {
        const fetchLocations = async (kind: "country" | "city") => {
          const params = new URLSearchParams({ q: search, kind });
          const response = await fetch(`/api/locations/search?${params.toString()}`, {
            signal: controller.signal,
            cache: "no-store",
          });
          if (!response.ok) {
            return [] as LocationSuggestion[];
          }

          const payload = (await response.json()) as { locations?: Array<Omit<LocationSuggestion, "kind">> };
          return (payload.locations ?? []).map((item) => ({
            ...item,
            kind,
            id: `${kind}:${item.id}`,
          }));
        };

        const [countryResults, cityResults] = await Promise.all([fetchLocations("country"), fetchLocations("city")]);
        const mergedResults = [...countryResults, ...cityResults];

        if (!controller.signal.aborted) {
          setResults(mergedResults);
        }
      } catch {
        if (!controller.signal.aborted) {
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [search, searchActive]);

  return {
    results: searchActive ? results : [],
    loading: searchActive ? loading : false,
  };
}

export function EventLocationPicker() {
  const copy = useMessages() as SiteCopy;
  const { eventLocation, setEventLocation, clearEventLocation, eventRadiusKm, setEventRadiusKm } = usePreferences();
  const [query, setQuery] = useState(eventLocation ? formatLocationLabel(eventLocation) : "");
  const [isEditing, setIsEditing] = useState(!eventLocation);

  const locationSearch = useLocationSearch(query, isEditing);

  const selectedLabel = useMemo(() => {
    if (!eventLocation) return "";
    const parts = [eventLocation.cityLabel ?? eventLocation.cityName, eventLocation.countryName].filter(Boolean);
    return parts.join(", ");
  }, [eventLocation]);
  const countrySelected = Boolean(eventLocation?.countryCode && !eventLocation.cityName);
  const radiusHelperText = countrySelected
    ? copy.ui.countryRadiusHint.replace("{location}", selectedLabel || eventLocation?.countryName || "--")
    : copy.ui.radiusHint;

  function chooseCountry(location: LocationSuggestion) {
    setEventLocation({
      countryCode: location.countryCode,
      countryName: location.countryName,
      cityName: null,
      cityLabel: null,
      latitude: location.latitude,
      longitude: location.longitude,
    });
    setQuery(location.countryName ?? location.label);
    setIsEditing(false);
  }

  function chooseCity(location: LocationSuggestion) {
    setEventLocation({
      countryCode: location.countryCode ?? eventLocation?.countryCode ?? null,
      countryName: location.countryName ?? eventLocation?.countryName ?? null,
      cityName: location.cityName,
      cityLabel: location.cityName ?? location.label,
      latitude: location.latitude,
      longitude: location.longitude,
    });
    setQuery(formatLocationLabel({
      countryCode: location.countryCode ?? eventLocation?.countryCode ?? null,
      countryName: location.countryName ?? eventLocation?.countryName ?? null,
      cityName: location.cityName,
      cityLabel: location.cityName ?? location.label,
      latitude: location.latitude,
      longitude: location.longitude,
    }));
    setIsEditing(false);
  }

  return (
    <section>
      <div className="mt-3 grid gap-3">
        {eventLocation && !isEditing ? (
          // Resolved-selection state. The label that surrounds this
          // component (rendered by `SiteShell` with the section header
          // "Event Location") already names what this is, so we don't
          // re-state "Selected location: …" here — the visible text
          // *is* the selection.
          <div className="rounded-2xl border border-border bg-muted p-3">
            <p className="text-sm font-medium text-foreground">{selectedLabel || "--"}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => {
                  setQuery(selectedLabel || "");
                  setIsEditing(true);
                }}
                className="rounded-xl border-border bg-muted text-foreground hover:bg-accent"
              >
                {copy.ui.changeLocation}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => {
                  clearEventLocation();
                  setQuery("");
                  setIsEditing(true);
                }}
                className="rounded-xl border-border bg-muted text-foreground hover:bg-accent"
              >
                {copy.ui.clear}
              </Button>
            </div>
          </div>
        ) : (
          // Editing state. Just an input — no second "Event Location"
          // label here; the section header above this component is
          // already labeling the field. The hint shown above used to
          // mention "OpenStreetMap" but that's plumbing detail
          // leaking into product copy, so it's been generalised.
          <div>
            {isEditing ? (
              <p className="-mt-2 mb-2 text-xs text-muted-foreground">
                {copy.ui.locationSearchHint}
              </p>
            ) : null}
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={copy.ui.searchLocationPlaceholder}
              className="w-full rounded-2xl border border-border bg-muted px-4 py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-rose-300/40"
            />
            {locationSearch.loading ? <p className="mt-2 text-xs text-muted-foreground">{copy.ui.loadingLocations}</p> : null}
            {locationSearch.results.length > 0 ? (
              <div className="mt-2 max-h-56 space-y-2 overflow-auto rounded-xl border border-border bg-card/70 p-2">
                {locationSearch.results.map((item) => {
                  // Build the visible row from the structured fields.
                  // Country rows show only the country name (the city
                  // chip would be an empty pill); city rows show
                  // "<City>, <Country>" so the user can disambiguate
                  // homonyms (Paris TX vs Paris FR) without us having
                  // to ship a noisy "city" / "country" badge.
                  const primary =
                    item.kind === "country"
                      ? item.countryName ?? item.label
                      : item.cityName ?? item.label;
                  const secondary =
                    item.kind === "country"
                      ? item.countryCode?.toUpperCase() ?? ""
                      : [item.countryName, item.countryCode?.toUpperCase()]
                          .filter(Boolean)
                          .join(" · ");
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() =>
                        item.kind === "country" ? chooseCountry(item) : chooseCity(item)
                      }
                      className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-left text-sm text-foreground transition hover:bg-accent"
                    >
                      <span className="block font-medium">{primary}</span>
                      {secondary ? (
                        <span className="block text-xs text-muted-foreground">{secondary}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : query.trim().length >= 2 && !locationSearch.loading ? (
              <p className="mt-2 text-xs text-muted-foreground">{copy.ui.noLocations}</p>
            ) : null}
            {eventLocation ? (
              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => setIsEditing(false)}
                  className="rounded-xl border-border bg-muted text-foreground hover:bg-accent"
                >
                  {copy.ui.done}
                </Button>
              </div>
            ) : null}
          </div>
        )}

        <div className="grid gap-2">
          <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{copy.ui.radiusKm}</label>
          <input
            type="number"
            min={10}
            max={2000}
            step={10}
            value={eventRadiusKm}
            disabled={countrySelected}
            onChange={(event) => setEventRadiusKm(Number(event.target.value))}
            className="w-full rounded-2xl border border-border bg-muted px-4 py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-rose-300/40 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <p className="text-[11px] text-muted-foreground">{radiusHelperText}</p>
        </div>
      </div>
    </section>
  );
}
